/**
 * PtyOutputCoalescer — batches raw PTY stdout before it is relayed to the S2
 * server.
 *
 * node-pty surfaces a busy TUI redraw as tens of small data events per second;
 * relaying each one as its own socket.io call made the server-side relay cost
 * (auth + ring append + room fan-out per frame) the bottleneck of the
 * Connected-TUI stream. Buffering for a short window and shipping one
 * concatenated frame preserves byte order and ANSI integrity (the relay treats
 * frames as an opaque byte stream — chunk boundaries carry no meaning) while
 * cutting the call rate by an order of magnitude.
 *
 * Flush triggers:
 *   - trailing timer (`flushMs`) after the first unflushed write,
 *   - synchronous size flush when the buffer reaches `maxBufferChars`
 *     (kept far below the 256KB PTY_FRAME_MAX_CHARS schema cap),
 *   - explicit `flush()` from the session on pty exit / teardown so the tail
 *     of output (exit messages, final repaint) is never lost.
 */

const DEFAULT_FLUSH_MS = 40;
const DEFAULT_MAX_BUFFER_CHARS = 48 * 1024;

export class PtyOutputCoalescer {
  private buffer = "";
  private timer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(
    private readonly sink: (data: string, dims: { cols: number; rows: number }) => void,
    private readonly getDims: () => { cols: number; rows: number },
    private readonly flushMs: number = DEFAULT_FLUSH_MS,
    private readonly maxBufferChars: number = DEFAULT_MAX_BUFFER_CHARS,
  ) {}

  /** Buffer a chunk; flushes synchronously when the size threshold is hit. */
  write(data: string): void {
    if (this.disposed || data === "") return;
    this.buffer += data;
    if (this.buffer.length >= this.maxBufferChars) {
      this.flush();
      return;
    }
    if (!this.timer) {
      this.timer = setTimeout(() => {
        this.flush();
      }, this.flushMs);
    }
  }

  /** Ship the buffered bytes now (no-op when empty). Dims are read at flush
   *  time so a mid-buffer resize stamps the frame with the dims it will render
   *  under. */
  flush(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.buffer === "") return;
    const data = this.buffer;
    this.buffer = "";
    this.sink(data, this.getDims());
  }

  /** Final flush, then drop any future writes (session torn down). Idempotent. */
  dispose(): void {
    if (this.disposed) return;
    this.flush();
    this.disposed = true;
  }
}
